import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, Play, Pause, ImageIcon, Award, ShieldCheck, Globe2 } from "lucide-react";

import naatiBirth1 from "@/assets/portfolio/naati-birth-cert-1.webp";
import naatiBirth2 from "@/assets/portfolio/naati-birth-cert-2.webp";
import naatiDeath from "@/assets/portfolio/naati-death-cert.webp";
import naatiName from "@/assets/portfolio/naati-name-change.webp";
import naatiReg from "@/assets/portfolio/naati-registered-personnel.webp";
import cidItaly from "@/assets/portfolio/cid-italy.webp";
import cidCzech from "@/assets/portfolio/cid-czech.webp";
import cidSaudi1 from "@/assets/portfolio/cid-saudi-1.webp";
import cidSaudi2 from "@/assets/portfolio/cid-saudi-2.webp";
import cidBrazil from "@/assets/portfolio/cid-brazil.webp";
import cidSpain1 from "@/assets/portfolio/cid-spain-1.webp";
import cidSpain2 from "@/assets/portfolio/cid-spain-2.webp";
import cidSpain3 from "@/assets/portfolio/cid-spain-3.webp";
import cidArgentina from "@/assets/portfolio/cid-argentina.webp";
import cidBahamas from "@/assets/portfolio/cid-bahamas.webp";

type PortfolioItem = {
  src: string;
  title: string;
  category: "NAATI Translation" | "Police Clearance";
  country: string;
  flag: string;
};

const ITEMS: PortfolioItem[] = [
  { src: naatiBirth1, title: "สูติบัตร แปลรับรอง NAATI", category: "NAATI Translation", country: "Australia", flag: "🇦🇺" },
  { src: naatiBirth2, title: "Birth Certificate – NAATI Certified", category: "NAATI Translation", country: "Australia", flag: "🇦🇺" },
  { src: naatiDeath, title: "มรณบัตร แปลรับรอง NAATI", category: "NAATI Translation", country: "Australia", flag: "🇦🇺" },
  { src: naatiName, title: "หนังสือเปลี่ยนชื่อ-สกุล NAATI", category: "NAATI Translation", country: "Australia", flag: "🇦🇺" },
  { src: naatiReg, title: "สด.8 / Registered Personnel NAATI", category: "NAATI Translation", country: "Australia", flag: "🇦🇺" },
  { src: cidItaly, title: "Police Clearance – Italy", category: "Police Clearance", country: "Italy", flag: "🇮🇹" },
  { src: cidCzech, title: "Police Clearance – Czech Republic", category: "Police Clearance", country: "Czech Republic", flag: "🇨🇿" },
  { src: cidSaudi1, title: "Police Clearance – Saudi Arabia", category: "Police Clearance", country: "Saudi Arabia", flag: "🇸🇦" },
  { src: cidSaudi2, title: "Police Clearance – Saudi Arabia", category: "Police Clearance", country: "Saudi Arabia", flag: "🇸🇦" },
  { src: cidBrazil, title: "Police Clearance – Brazil", category: "Police Clearance", country: "Brazil", flag: "🇧🇷" },
  { src: cidSpain1, title: "Police Clearance – Spain", category: "Police Clearance", country: "Spain", flag: "🇪🇸" },
  { src: cidSpain2, title: "Police Clearance – Spain", category: "Police Clearance", country: "Spain", flag: "🇪🇸" },
  { src: cidSpain3, title: "Police Clearance – Spain", category: "Police Clearance", country: "Spain", flag: "🇪🇸" },
  { src: cidArgentina, title: "Police Clearance – Argentina", category: "Police Clearance", country: "Argentina", flag: "🇦🇷" },
  { src: cidBahamas, title: "Police Clearance – Bahamas", category: "Police Clearance", country: "Bahamas", flag: "🇧🇸" },
];

type Filter = "all" | "NAATI Translation" | "Police Clearance";

export function PortfolioShowcase() {
  const [filter, setFilter] = useState<Filter>("all");
  const [slideIndex, setSlideIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const filtered = filter === "all" ? ITEMS : ITEMS.filter((i) => i.category === filter);

  // Auto-advance slideshow
  useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      setSlideIndex((i) => (i + 1) % ITEMS.length);
    }, 4000);
    return () => clearInterval(id);
  }, [isPlaying]);

  const goPrev = useCallback(() => setSlideIndex((i) => (i - 1 + ITEMS.length) % ITEMS.length), []);
  const goNext = useCallback(() => setSlideIndex((i) => (i + 1) % ITEMS.length), []);

  // Lightbox keyboard
  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i === null ? 0 : (i + 1) % filtered.length));
      if (e.key === "ArrowLeft") setLightbox((i) => (i === null ? 0 : (i - 1 + filtered.length) % filtered.length));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, filtered.length]);

  const current = ITEMS[slideIndex];
  const naatiCount = ITEMS.filter((i) => i.category === "NAATI Translation").length;
  const cidCount = ITEMS.filter((i) => i.category === "Police Clearance").length;
  const uniqueCountries = new Set(ITEMS.map((i) => i.country)).size;

  return (
    <section id="portfolio" className="relative overflow-hidden bg-gradient-to-b from-background via-muted/30 to-background py-20 md:py-28">
      {/* decorative bg */}
      <div aria-hidden className="absolute inset-0 -z-10 opacity-[0.04]" style={{ backgroundImage: "var(--grid-pattern)", backgroundSize: "48px 48px" }} />
      <div aria-hidden className="absolute -right-40 top-20 -z-10 h-[500px] w-[500px] rounded-full bg-gold/10 blur-3xl" />
      <div aria-hidden className="absolute -left-40 bottom-0 -z-10 h-[500px] w-[500px] rounded-full bg-primary/10 blur-3xl" />

      <div className="mx-auto max-w-7xl px-4">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold-foreground">
            <Award className="h-3.5 w-3.5" />
            ผลงานจริงของลูกค้า
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-5xl">
            <span className="bg-gradient-to-r from-primary via-gold to-primary bg-clip-text text-transparent">
              Portfolio & Gallery
            </span>
          </h2>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            ตัวอย่างผลงานแปลรับรอง NAATI และ Police Clearance ที่เราจัดทำให้ลูกค้าจริง
            <br className="hidden md:block" />
            <span className="text-sm">* ข้อมูลส่วนบุคคลถูกเบลอเพื่อความเป็นส่วนตัวของลูกค้า</span>
          </p>
        </div>

        {/* Stats strip */}
        <div className="mx-auto mt-10 grid max-w-4xl grid-cols-3 gap-4">
          <StatChip icon={ImageIcon} label="ผลงานตัวอย่าง" value={`${ITEMS.length}+`} />
          <StatChip icon={Globe2} label="ประเทศปลายทาง" value={`${uniqueCountries}+`} />
          <StatChip icon={ShieldCheck} label="ลูกค้ารวม" value="1,200+" />
        </div>

        {/* SLIDESHOW HERO */}
        <div className="mt-12 overflow-hidden rounded-3xl border border-border bg-card shadow-2xl">
          <div className="grid lg:grid-cols-5">
            {/* Slide image */}
            <div className="relative min-w-0 lg:col-span-3">
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-gradient-to-br from-primary/5 to-gold/5">
                {ITEMS.map((item, i) => (
                  <img
                    key={item.src}
                    src={item.src}
                    alt={item.title}
                    width={1200}
                    height={900}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                    className={`absolute inset-0 h-full w-full object-contain transition-all duration-700 ${
                      i === slideIndex ? "scale-100 opacity-100" : "scale-105 opacity-0"
                    }`}
                  />
                ))}

                {/* Nav arrows */}
                <button
                  onClick={goPrev}
                  aria-label="ก่อนหน้า"
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-background/90 p-2.5 text-foreground shadow-lg backdrop-blur transition hover:scale-110 hover:bg-background"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={goNext}
                  aria-label="ถัดไป"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-background/90 p-2.5 text-foreground shadow-lg backdrop-blur transition hover:scale-110 hover:bg-background"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>

                {/* Play/Pause */}
                <button
                  onClick={() => setIsPlaying((p) => !p)}
                  aria-label={isPlaying ? "หยุดสไลด์" : "เล่นสไลด์"}
                  className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-foreground/85 px-3 py-1.5 text-xs font-semibold text-background backdrop-blur transition hover:bg-foreground"
                >
                  {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                  {isPlaying ? "Auto" : "Paused"}
                </button>

                {/* Counter */}
                <div className="absolute bottom-4 left-4 rounded-full bg-foreground/85 px-3 py-1.5 text-xs font-semibold text-background backdrop-blur">
                  {String(slideIndex + 1).padStart(2, "0")} / {String(ITEMS.length).padStart(2, "0")}
                </div>
              </div>
            </div>

            {/* Slide caption */}
            <div className="flex min-w-0 flex-col justify-between bg-gradient-to-br from-primary to-primary/90 p-6 text-primary-foreground sm:p-8 lg:col-span-2">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-gold/20 px-3 py-1 text-xs font-semibold text-gold ring-1 ring-gold/40">
                  {current.category}
                </div>
                <h3 className="text-2xl font-bold leading-tight md:text-3xl">{current.title}</h3>
                <div className="mt-4 flex items-center gap-3 text-base">
                  <span className="text-3xl">{current.flag}</span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-primary-foreground/85">ใช้สำหรับประเทศ</div>
                    <div className="font-semibold">{current.country}</div>
                  </div>
                </div>

                <ul className="mt-6 space-y-2 text-sm text-primary-foreground/95">
                  <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" />รับรองโดยนักแปล / ทนาย Notary ขึ้นทะเบียน</li>
                  <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" />ยอมรับโดยหน่วยงานปลายทางจริง</li>
                  <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" />จัดส่งทั่วประเทศ + Digital Copy</li>
                </ul>
              </div>

              {/* Thumbnail strip */}
              <div className="mt-6 flex max-w-full gap-2 overflow-x-auto pb-1">
                {ITEMS.map((item, i) => (
                  <button
                    key={item.src}
                    onClick={() => setSlideIndex(i)}
                    aria-label={`ดูสไลด์ที่ ${i + 1}`}
                    className={`relative h-14 w-14 shrink-0 overflow-hidden rounded-lg ring-2 transition ${
                      i === slideIndex ? "ring-gold scale-105" : "ring-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={item.src} alt={item.title} width={56} height={56} className="h-full w-full object-cover" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* FILTER + GALLERY */}
        <div className="mt-16">
          <div className="mb-8 flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div>
              <h3 className="text-2xl font-bold text-foreground md:text-3xl">Gallery ผลงานทั้งหมด</h3>
              <p className="mt-1 text-sm text-muted-foreground">คลิกที่รูปเพื่อขยายดูรายละเอียด</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <FilterBtn active={filter === "all"} onClick={() => setFilter("all")} count={ITEMS.length}>
                ทั้งหมด
              </FilterBtn>
              <FilterBtn active={filter === "NAATI Translation"} onClick={() => setFilter("NAATI Translation")} count={naatiCount}>
                NAATI Translation
              </FilterBtn>
              <FilterBtn active={filter === "Police Clearance"} onClick={() => setFilter("Police Clearance")} count={cidCount}>
                Police Clearance
              </FilterBtn>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((item, i) => (
              <button
                key={item.src}
                onClick={() => setLightbox(i)}
                className="group relative aspect-[3/4] overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-muted to-muted/40 shadow-md transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <img
                  src={item.src}
                  alt={item.title}
                  width={600}
                  height={800}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/30 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                <div className="absolute inset-x-0 bottom-0 translate-y-2 p-3 text-left text-primary-foreground opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100">
                  <div className="mb-1 inline-block rounded-full bg-gold/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gold ring-1 ring-gold/50">
                    {item.flag} {item.country}
                  </div>
                  <div className="line-clamp-2 text-xs font-semibold leading-snug">{item.title}</div>
                </div>
                <div className="absolute right-2 top-2 rounded-full bg-background/90 px-2 py-0.5 text-[10px] font-bold text-primary backdrop-blur">
                  {item.flag}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* LIGHTBOX */}
      {lightbox !== null && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            aria-label="ปิด"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
          >
            <X className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((i) => (i === null ? 0 : (i - 1 + filtered.length) % filtered.length));
            }}
            aria-label="ก่อนหน้า"
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((i) => (i === null ? 0 : (i + 1) % filtered.length));
            }}
            aria-label="ถัดไป"
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          <div className="relative max-h-[90vh] max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={filtered[lightbox].src}
              alt={filtered[lightbox].title}
              width={1200}
              height={1600}
              decoding="async"
              className="max-h-[85vh] w-auto rounded-xl object-contain shadow-2xl"
            />
            <div className="mt-3 rounded-xl bg-white/10 p-3 text-center text-white backdrop-blur">
              <div className="text-sm font-semibold">{filtered[lightbox].title}</div>
              <div className="text-xs opacity-95">
                {filtered[lightbox].flag} {filtered[lightbox].country} · {filtered[lightbox].category} · {lightbox + 1} / {filtered.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function StatChip({ icon: Icon, label, value }: { icon: typeof ImageIcon; label: string; value: string }) {
  return (
    <div className="flex items-center justify-center gap-3 rounded-2xl border border-border bg-card px-4 py-4 shadow-sm">
      <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-left">
        <div className="text-xl font-bold text-foreground md:text-2xl">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function FilterBtn({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
        active
          ? "border-primary bg-primary text-primary-foreground shadow-md"
          : "border-border bg-card text-foreground hover:border-primary/40 hover:bg-muted"
      }`}
    >
      {children}
      <span className={`rounded-full px-2 py-0.5 text-xs ${active ? "bg-primary-foreground/20" : "bg-muted"}`}>
        {count}
      </span>
    </button>
  );
}
